import { ref } from 'vue';
import { defineStore } from 'pinia';

import { Logger } from '@/services/logger';
import { UCV3ExtendedWindow } from '@/types/usercentrics/v3';
import { ConsentDetails } from '@/types/usercentrics/v3/interfaces';
import {
  getLocalTrackingDataTimestampMs,
  getLocalUserInteraction,
  getTrackingSettingsId,
  removeLocalCMPTrackingData,
  saveChangeEventTimestampLocally,
  saveChangeEventUserInteractionLocally,
  saveConsentServicesLocally,
  saveInitializationControllerIdLocally,
  TCF_STUB_SCRIPT_URL,
  USERCENTRICS_SCRIPT_ID,
  USERCENTRICS_SCRIPT_URL,
} from '@/utils/tracking/common';
import { getUC, isTCFEnabled } from '@/utils/tracking/usercentricsV3';
import { handleError } from '@/utils/errors';
import {
  getUserTrackingConsents,
  TrackingConsentDataV1,
  TrackingConsentMappingV1,
  updateUserTracking,
} from '@/api/bff/tracking';

export const useTrackingStore = defineStore('tracking', () => {
  /**
   * If `true` then the usercentrics script was loaded and usercentrics object is available to use.
   */
  const isTrackingInitialized = ref(false);
  /**
   * ControllerId of the logged-in user
   */
  const userControllerId = ref<string | null>(null);
  /**
   * Tracking consents of the logged-in user
   */
  const userTrackingConsents = ref<TrackingConsentMappingV1 | null>(null);
  /**
   * ControllerId with which the Usercentrics script was initialized
   */
  const initialControllerId = ref<string | null>(null);
  /**
   * Usercentrics settingsId that should be used for initializing the Usercentrics script on the current domain and environment.
   */
  const usercentricsSettingsId = ref<string | null>(null);

  function resetTrackingStore() {
    isTrackingInitialized.value = false;
    userControllerId.value = null;
    userTrackingConsents.value = null;
    initialControllerId.value = null;
    usercentricsSettingsId.value = null;
  }

  function setTrackingSettings() {
    const settingsId = getTrackingSettingsId();

    if (!settingsId) {
      throw new Error('Missing usercentrics settings id variable for current domain/environment');
    }

    usercentricsSettingsId.value = settingsId;
  }

  async function fetchUserTrackingConsents() {
    try {
      const result = await getUserTrackingConsents();

      if (result) {
        userTrackingConsents.value = result.trackingConsents || null;
        return userTrackingConsents.value;
      }
    } catch (error) {
      Logger.logError('Error fetching user tracking consents');
    }
  }

  function isLocalTrackingDataNewest() {
    const doesTrackingDataExistLocally = checkDoesTrackingDataExistLocally();

    // only local tracking data exists
    if (!userTrackingConsents.value && doesTrackingDataExistLocally) {
      return true;
    }

    const localTrackingDataTimestampMs = getLocalTrackingDataTimestampMs();
    // both local and stored tracking data exist
    if (
      userTrackingConsents.value &&
      doesTrackingDataExistLocally &&
      localTrackingDataTimestampMs
    ) {
      const localTimeInMilliSeconds = Number(localTrackingDataTimestampMs);
      const fetchedTimeInMilliSeconds = Number(userTrackingConsents.value.timestamp);

      return localTimeInMilliSeconds > fetchedTimeInMilliSeconds;
    }

    return false;
  }

  async function sendTrackingData(consentDetails: ConsentDetails) {
    try {
      const controllerId = initialControllerId.value || (await getUC().getControllerId());
      const payload = generateUpdateTrackingConsentPayload({ consentDetails, controllerId });

      await updateUserTracking(payload);
    } catch (err) {
      handleError(err);
    }
  }

  function generateUpdateTrackingConsentPayload({
    consentDetails,
    controllerId,
  }: {
    consentDetails: ConsentDetails;
    controllerId: string;
  }) {
    return {
      controllerId,
      timestamp: `${getConsentTimestamp(consentDetails)}`,
      status: consentDetails.consent.status,
      services: generateServicePayload(consentDetails.services),
      categories: generateCategoriesPayload(consentDetails.categories),
      categoryLookup: generateCategoryLookup(consentDetails.categories),
    };
  }

  function generateCategoriesPayload(categories: ConsentDetails['categories']) {
    return Object.entries(categories).reduce((acc, [categorySlug, category]) => {
      const { essential, state, dps } = category;

      acc = {
        ...acc,
        [categorySlug]: {
          state,
          ...(dps ? { dps } : {}),
          essential: !!essential, // not all categories have the essential property
        },
      };
      return acc;
    }, {});
  }

  function generateServicePayload(services: ConsentDetails['services']) {
    return Object.entries(services).reduce(
      (acc, [serviceId, { name, version, category, essential, consent }]) => {
        const service = {
          name,
          version,
          category,
          essential,
          consent: {
            given: !!consent?.given, // not all services have the consent property
          },
        };

        acc = {
          ...acc,
          [serviceId]: service,
        };

        return acc;
      },
      {} as TrackingConsentDataV1['services'],
    );
  }

  function generateCategoryLookup(categories: ConsentDetails['categories']) {
    return Object.entries(categories).map(([categorySlug, category]) => ({
      slug: categorySlug,
      label: category.name ?? categorySlug,
    }));
  }

  function generateConsentServicesForStoringLocally(services: ConsentDetails['services']) {
    return Object.values(services).reduce(
      (acc, { name, consent }) => {
        acc = {
          ...acc,
          [name]: !!consent?.given,
        };

        return acc;
      },
      {} as Record<string, boolean>,
    );
  }

  function saveConsentServicesToLocalStorage({ services }: ConsentDetails) {
    const data = generateConsentServicesForStoringLocally(services);
    saveConsentServicesLocally(data);
  }

  function getConsentTimestamp(consentDetails: ConsentDetails) {
    return consentDetails.consent.updatedAt || consentDetails.consent.createdAt;
  }

  /**
   * Transparency & Consent Framework (TCF) script info: https://usercentrics.com/docs/web/migration/migration-from-v2/#script-tag
   */
  function generateTCFStubScriptElement() {
    const tcfStubScript = document.createElement('script');
    tcfStubScript.type = 'text/javascript';
    tcfStubScript.src = TCF_STUB_SCRIPT_URL;

    return tcfStubScript;
  }

  function generateUsercentricsScriptElement(controllerId?: string | null) {
    if (!usercentricsSettingsId.value) {
      throw new Error('Missing usercentrics settings id variable');
    }

    const usercentricsScript = document.createElement('script');
    usercentricsScript.type = 'text/javascript';

    usercentricsScript.setAttribute('id', USERCENTRICS_SCRIPT_ID);
    usercentricsScript.setAttribute('data-settings-id', usercentricsSettingsId.value);

    if (isTCFEnabled()) {
      usercentricsScript.setAttribute('data-tcf-enabled', 'true'); // enable TCF
    }

    if (controllerId) {
      usercentricsScript.setAttribute('data-controller-id', controllerId);
    }

    usercentricsScript.src = USERCENTRICS_SCRIPT_URL;

    return usercentricsScript;
  }

  async function initializeTracking(controllerId?: string | null) {
    const elementForAppendingScripts = document.head;

    if (elementForAppendingScripts) {
      if (isTCFEnabled()) {
        elementForAppendingScripts.appendChild(generateTCFStubScriptElement());
      }

      elementForAppendingScripts.appendChild(generateUsercentricsScriptElement(controllerId));
    }
  }

  async function clearLocalTrackingData() {
    removeLocalCMPTrackingData();
    await usercentrics()?.clearUserSession();
  }

  async function getConsentDetails() {
    const details = await usercentrics()?.getConsentDetails();

    return details;
  }

  /**
   * Checks if user interacted with the consents. This is used before Usercentrics is initialized.
   */
  function checkDoesTrackingDataExistLocally() {
    return getLocalUserInteraction();
  }

  async function showConsentsBannerFirstLayer() {
    await usercentrics()?.showFirstLayer();
  }

  async function showConsentsBannerSecondLayer() {
    await usercentrics()?.showSecondLayer();
  }

  /**
   * Usercentrics object that was initialized with the usercentrics script.
   */
  function usercentrics() {
    return (window as unknown as UCV3ExtendedWindow).__ucCmp;
  }

  function suppressUsercentricsCMPDisplay() {
    Logger.logInfo('Suppressing UC');
    (window as unknown as UCV3ExtendedWindow).UC_UI_SUPPRESS_CMP_DISPLAY = true;
  }

  return {
    isTrackingInitialized,
    initializeTracking,
    usercentrics,
    clearLocalTrackingData,
    getConsentDetails,
    initialControllerId,
    sendTrackingData,
    getUserTrackingConsents: fetchUserTrackingConsents,
    setTrackingSettings,
    checkDoesTrackingDataExistLocally,
    resetTrackingStore,
    userTrackingConsents,
    showConsentsBannerFirstLayer,
    showConsentsBannerSecondLayer,
    saveInitializationControllerIdLocally,
    saveChangeEventTimestampLocally,
    saveChangeEventUserInteractionLocally,
    isLocalTrackingDataNewest,
    usercentricsSettingsId,
    generateUpdateTrackingConsentPayload,
    suppressUsercentricsCMPDisplay,
    getConsentTimestamp,
    saveConsentServicesToLocalStorage,
  };
});
